import { Modal, message } from 'antd';
import React from 'react';
import SpaceDetailPanel from '../SpaceDetailPanel';
import styles from './index.module.less';

type SpaceDetailContextValue = {
  openSpaceDetail: (spaceUuid: string) => void;
};

const SpaceDetailContext = React.createContext<SpaceDetailContextValue | null>(
  null,
);

const fallbackOpenSpaceDetail = (spaceUuid: string) => {
  if (!spaceUuid) {
    message.warning('该房源缺少 UUID，无法打开详情');
    return;
  }
  message.warning('房源详情暂不可用，请刷新页面后重试');
};

export function useSpaceDetail() {
  return (
    React.useContext(SpaceDetailContext) ?? {
      openSpaceDetail: fallbackOpenSpaceDetail,
    }
  );
}

export function SpaceDetailProvider({ children }: React.PropsWithChildren) {
  const [spaceUuid, setSpaceUuid] = React.useState<string>();
  const [open, setOpen] = React.useState(false);

  const openSpaceDetail = React.useCallback((uuid: string) => {
    const normalized = String(uuid || '').trim();
    if (!normalized) {
      message.warning('该房源缺少 UUID，无法打开详情');
      return;
    }
    setSpaceUuid(normalized);
    setOpen(true);
  }, []);

  const close = React.useCallback(() => setOpen(false), []);

  const value = React.useMemo(() => ({ openSpaceDetail }), [openSpaceDetail]);

  return (
    <SpaceDetailContext.Provider value={value}>
      {children}
      <Modal
        open={open}
        onCancel={close}
        title="房间详情"
        footer={null}
        width="min(1440px, calc(100vw - 48px))"
        centered
        destroyOnHidden
        keyboard
        closable={{ 'aria-label': '关闭' }}
        className={styles.modal}
        styles={{
          body: {
            padding: 16,
            maxHeight: 'calc(100dvh - 120px)',
            overflow: 'auto',
            overscrollBehavior: 'contain',
          },
        }}
      >
        {spaceUuid ? <SpaceDetailPanel spaceUuid={spaceUuid} /> : null}
      </Modal>
    </SpaceDetailContext.Provider>
  );
}
